import React, { useMemo, useState } from 'react';
import { Alert, Button, DatePicker, Form, Input, Modal, Select } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import moment from 'moment';
import { isMobile } from 'react-device-detect';
import { useDispatch } from 'react-redux';
import { checkPermission } from '@/utils/permission';
import { reportCustomerServiceStaffFailure } from '@/redux/actions/customerServicesActions';

const DOCTOR_ASSIGNMENT_SOURCES = [
    ['examination_doctors', 'examination', 'Bác sĩ khám'],
    ['treatment_doctors', 'treatment', 'Bác sĩ điều trị'],
    ['implant_doctors', 'implant_placement', 'Bác sĩ cắm trụ'],
    ['prosthetic_doctors', 'prosthetic_restoration', 'Bác sĩ phục hình'],
];

const currentDoctorAssignments = customerService => {
    const rows = DOCTOR_ASSIGNMENT_SOURCES.flatMap(([relation, componentKey, componentLabel]) => (
        (customerService?.[relation] || []).map(assignment => ({
            ...assignment,
            component_key: assignment.component_key || componentKey,
            component_label: componentLabel,
        }))
    ));
    if (rows.length) return Array.from(new Map(rows.map(row => [Number(row.id), row])).values());

    // Chỉ fallback cho dữ liệu legacy chưa có các component bác sĩ độc lập.
    return (customerService?.doctors || [])
        .filter(assignment => assignment.assignment_kind !== 'APPRENTICE' && !Number(assignment.is_apprentice))
        .map(assignment => ({ ...assignment, component_key: 'full_service', component_label: 'Bác sĩ điều trị' }));
};

const newIdempotencyKey = () => `case-failed-${Date.now()}-${Math.random().toString(36).slice(2)}`;

export default function CustomerServiceFailureReportForm({ customerService, doctors = [], onReported }) {
    const dispatch = useDispatch();
    const [form] = Form.useForm();
    const [visible, setVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [requestKey, setRequestKey] = useState(null);
    const [selectedAssignmentIds, setSelectedAssignmentIds] = useState([]);
    const assignments = useMemo(() => currentDoctorAssignments(customerService), [customerService]);
    const selectedEmployeeIds = assignments.filter(row => selectedAssignmentIds.includes(Number(row.id)))
        .map(row => Number(row.employee_id));
    const replacementOptions = (doctors || []).filter(option => (
        !selectedEmployeeIds.includes(Number(option.user_id))
    ));
    const allowReport = checkPermission('customer_service_staff_change', 'report');

    if (!allowReport || !assignments.length) return null;

    const open = () => {
        setRequestKey(newIdempotencyKey());
        setSelectedAssignmentIds([]);
        form.setFieldsValue({
            from_assignment_ids: [],
            to_employee_ids: [],
            effective_at: moment(),
            note: '',
        });
        setVisible(true);
    };

    const close = () => {
        if (loading) return;
        setVisible(false);
        form.resetFields();
    };

    const submit = async () => {
        const values = await form.validateFields();
        setLoading(true);
        try {
            await dispatch(reportCustomerServiceStaffFailure(customerService.id, {
                from_assignment_ids: values.from_assignment_ids.map(Number),
                to_employee_ids: values.to_employee_ids.map(Number),
                effective_at: values.effective_at.format('YYYY-MM-DD HH:mm:ss'),
                note: values.note.trim(),
                idempotency_key: requestKey,
            }));
            setVisible(false);
            form.resetFields();
            if (typeof onReported === 'function') await onReported();
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Button danger icon={<WarningOutlined />} onClick={open}>Báo cáo ca không thành công</Button>
            <Modal
                visible={visible}
                title="Báo cáo ca không thành công"
                okText="Ghi nhận và chuyển giao"
                cancelText="Hủy"
                confirmLoading={loading}
                onOk={submit}
                onCancel={close}
                maskClosable={false}
                width={isMobile ? '100%' : '55%'}
            >
                <Alert
                    type="warning"
                    message="Khi ghi nhận, hệ thống sẽ chuyển giao từng phạm vi đã chọn và tự động gửi yêu cầu thu hồi phần hoa hồng tương ứng sang Điều chỉnh hoa hồng."
                    style={{ marginBottom: 16 }}
                />
                <Form form={form} layout="vertical">
                    <Form.Item name="from_assignment_ids" label="Bác sĩ bị fail và phạm vi" rules={[{ required: true, type: 'array', min: 1, message: 'Vui lòng chọn ít nhất một bác sĩ và phạm vi công việc.' }]}>
                        <Select
                            mode="multiple"
                            showSearch
                            optionFilterProp="children"
                            placeholder="Chọn bác sĩ đang phụ trách"
                            onChange={values => {
                                setSelectedAssignmentIds(values.map(Number));
                                form.setFieldsValue({ to_employee_ids: [] });
                            }}
                        >
                            {assignments.map(assignment => (
                                <Select.Option key={assignment.id} value={assignment.id}>
                                    {assignment.component_label} — {assignment?.doctor?.full_name || assignment?.employee?.full_name || 'Chưa xác định'}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item name="to_employee_ids" label="Bác sĩ tiếp nhận" rules={[{ required: true, type: 'array', min: 1, message: 'Vui lòng chọn ít nhất một bác sĩ tiếp nhận.' }]}>
                        <Select
                            mode="multiple"
                            showSearch
                            optionFilterProp="children"
                            placeholder="Chọn bác sĩ tiếp nhận"
                            style={{ width: '100%' }}
                        >
                            {replacementOptions.map(option => (
                                <Select.Option key={option.user_id} value={option.user_id}>
                                    {option.display_name || option.full_name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item name="effective_at" label="Thời điểm hiệu lực" rules={[{ required: true, message: 'Vui lòng chọn thời điểm hiệu lực.' }]}>
                        <DatePicker showTime={{ format: 'HH:mm' }} format="DD/MM/YYYY HH:mm" style={{ width: '100%' }} disabledDate={date => date && date.endOf('day') > moment().endOf('day')} />
                    </Form.Item>
                    <Form.Item name="note" label="Mô tả nguyên nhân" rules={[{ required: true, whitespace: true, message: 'Vui lòng mô tả nguyên nhân ca không thành công.' }]}>
                        <Input.TextArea rows={3} maxLength={1000} placeholder="Nhập lý do và bối cảnh của ca không thành công" />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
}
